'use client';

import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { capture } from '@tagadapay/analytics';

import { SetupExperience } from '@/components/setup/experience/SetupExperience';
import { QuickStartHub } from '@/components/setup/quick-start/QuickStartHub';
import { shouldShowQuickStartHub } from '@/lib/setupFeatures';
import { useStoresList } from '@/lib/api/stores';

export function SoloSetupPage() {
  const [searchParams] = useSearchParams();
  const { data: storesData, isLoading } = useStoresList({
    pagination: { page: 1, pageSize: 1 },
  });
  const hasStores = (storesData?.stores?.length ?? 0) > 0;
  const showHub = shouldShowQuickStartHub(hasStores, searchParams);

  // Fire once the branch below is actually decided, from ABOVE it, so a merchant
  // routed to the hub is recorded as having arrived. `setup_step_viewed` only
  // exists inside the wizard, which is why hub arrivals — and merchants held on
  // a recovery screen — used to be indistinguishable from merchants who never
  // clicked through from the dashboard at all.
  const lastSentScreenRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    const screen = showHub ? 'hub' : 'wizard';
    if (lastSentScreenRef.current === screen) return;
    lastSentScreenRef.current = screen;
    capture('setup_landing_viewed', { screen, has_store: hasStores });
  }, [isLoading, showHub, hasStores]);

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    );
  }

  return showHub ? <QuickStartHub /> : <SetupExperience />;
}
